import { useEffect, useState } from "react";
import { useSearchParams, useLocation, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, Croissant } from "lucide-react";

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const location = useLocation();
  const isCancel = location.pathname.includes("cancel");
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ loading: !!sessionId, paid: false, meta: {} });

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!sessionId) { setState({ loading: false, paid: false, meta: {} }); return; }
    let tries = 0, active = true;
    const run = async () => {
      let data = null;
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        data = res.data;
      } catch (e) {
        console.error("Payment status fetch failed:", e);
      }
      if (!active) return;
      const meta = (data && data.metadata) || {};
      if (isCancel) { setState({ loading: false, paid: false, meta }); return; }
      if (data && data.payment_status === "paid") { setState({ loading: false, paid: true, meta }); return; }
      if (data && (data.payment_status === "failed" || data.status === "expired")) { setState({ loading: false, paid: false, meta }); return; }
      if (tries++ < 10 && active) { setState((s) => ({ ...s, meta })); setTimeout(run, 1500); }
      else if (active) setState((s) => ({ ...s, loading: false, meta }));
    };
    run();
    return () => { active = false; };
  }, [sessionId, isCancel]);

  const meta = state.meta || {};
  const isSub = meta.type === "subscription";

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream paper-grain px-5">
      <div className="w-full max-w-md rounded-2xl border border-cocoa/10 bg-warmwhite p-8 text-center shadow-[0_12px_40px_rgba(64,53,47,0.08)]">
        {state.loading ? (
          <>
            <Loader2 className="mx-auto mb-4 h-10 w-10 animate-spin text-terracotta" />
            <h1 className="font-serif text-2xl text-cocoa">Confirming your payment…</h1>
            <p className="mt-2 text-sm text-cocoa/60">This only takes a moment.</p>
          </>
        ) : state.paid ? (
          <>
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-sage"><CheckCircle2 className="h-9 w-9 text-white" strokeWidth={1.6} /></div>
            <h1 className="font-serif text-3xl font-semibold text-cocoa">{isSub ? "You're all set!" : "Payment received!"}</h1>
            {isSub ? (
              <>
                <p className="mt-2 text-sm text-cocoa/60">Your subscription is active. Thank you for supporting Oven &amp; Order.</p>
                <Link to="/dashboard/settings"><Button className="mt-6 rounded-full bg-terracotta px-8 py-6 text-warmwhite hover:bg-terracotta/90" data-testid="payment-back-dashboard">Back to dashboard</Button></Link>
              </>
            ) : (
              <>
                <p className="mt-2 text-sm text-cocoa/60">Your preorder{meta.order_number ? ` #${meta.order_number}` : ""} is paid and confirmed. The bakery has been notified.</p>
                {meta.slug && <Link to={`/bakery/${meta.slug}`}><Button className="mt-6 rounded-full bg-terracotta px-8 py-6 text-warmwhite hover:bg-terracotta/90" data-testid="payment-back-store">Back to the bakery</Button></Link>}
              </>
            )}
          </>
        ) : isCancel ? (
          <>
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-cocoa/10"><Croissant className="h-8 w-8 text-cocoa/50" strokeWidth={1.4} /></div>
            <h1 className="font-serif text-3xl font-semibold text-cocoa">Payment canceled</h1>
            <p className="mt-2 text-sm text-cocoa/60">No charge was made.{!isSub ? " Your preorder is still saved — you can pay at pickup or try again." : ""}</p>
            {!isSub && meta.slug ? (
              <Link to={`/bakery/${meta.slug}`}><Button variant="outline" className="mt-6 rounded-full border-cocoa/20 px-8 py-6 text-cocoa" data-testid="payment-cancel-back">Back to the bakery</Button></Link>
            ) : (
              <Link to="/dashboard/settings"><Button variant="outline" className="mt-6 rounded-full border-cocoa/20 px-8 py-6 text-cocoa" data-testid="payment-cancel-back">Back to dashboard</Button></Link>
            )}
          </>
        ) : (
          <>
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10"><XCircle className="h-9 w-9 text-destructive" strokeWidth={1.5} /></div>
            <h1 className="font-serif text-3xl font-semibold text-cocoa">We couldn't confirm payment</h1>
            <p className="mt-2 text-sm text-cocoa/60">If you were charged, it will reflect shortly. Please contact the bakery if this persists.</p>
            <Link to="/"><Button variant="outline" className="mt-6 rounded-full border-cocoa/20 px-8 py-6 text-cocoa">Back home</Button></Link>
          </>
        )}
      </div>
    </div>
  );
}
